import webmcpTradutor, { type WebMcpTranslation } from "@/tradutors/webmcp";
import type { WebMcpToolsContext } from "@/composables/webmcp/shared";

/**
 * Adaptador do WebMCP — o ÚNICO ponto do projeto que toca a API do browser.
 *
 * A spec ainda está em origin trial e já mudou de namespace uma vez
 * (`navigator.modelContext` → `document.modelContext`) e de nome da callback
 * (`handler` → `execute`). Concentrando a detecção aqui, quando ela mudar de
 * novo mexemos num arquivo só, e não nas ~28 tools.
 *
 * Requisitos do browser (nada a fazer no app, mas bom saber ao depurar):
 * - secure context: HTTPS ou localhost;
 * - documento origin-isolated: basta o servidor NÃO mandar
 *   `Origin-Agent-Cluster: ?0` — com esse header a API some sem aviso;
 * - Permissions Policy `tools`, cujo default `self` já libera o top-level.
 */

/** Cookie gravado por `ModalLegalWarning.vue` quando o aviso 18+ é aceito. */
const LEGAL_WARNING_COOKIE = "legalWarning";

/**
 * Evento disparado por `ModalLegalWarning.vue` no aceite. Sem ele só restaria
 * ficar em polling no document.cookie para saber quando o gate abriu.
 */
export const LEGAL_WARNING_ACCEPTED_EVENT = "bds:legal-warning-accepted";

/** Container do WebMCP, ou null quando o browser não tem a API. */
export function getModelContext(): ModelContextContainer | null {
  if (!import.meta.client) return null;
  return document.modelContext ?? navigator.modelContext ?? null;
}

export function isWebMcpAvailable(): boolean {
  return getModelContext() !== null;
}

/** O aviso legal 18+ já foi aceito neste browser? */
export function hasAcceptedLegalWarning(): boolean {
  if (!import.meta.client) return false;
  return document.cookie
    .split(";")
    .some((c) => c.trim().startsWith(`${LEGAL_WARNING_COOKIE}=`));
}

/** Textos das tools no idioma da rota. */
export function webmcpTranslation(language: unknown): WebMcpTranslation {
  return String(language) === "en"
    ? webmcpTradutor.ingles
    : webmcpTradutor.portugues;
}

/**
 * Quem está com cada nome de tool registrado agora (o signal do registro).
 *
 * O browser recusa um nome repetido, e isso acontece numa navegação comum: o
 * Nuxt monta a página nova ANTES de desmontar a antiga, então em
 * /modelos/ana/video → /modelos/bia/video o player novo tentava registrar
 * `control_video` com o do antigo ainda de pé — e ficava sem a tool depois que
 * o antigo saía. Com o dono anotado, o registro novo espera o antigo soltar.
 */
const donosDosNomes = new Map<string, AbortSignal>();

function nomeLiberado(nome: string): Promise<void> {
  const dono = donosDosNomes.get(nome);
  if (!dono || dono.aborted) return Promise.resolve();
  return new Promise((resolve) => {
    // O browser desregistra no próprio abort; o setTimeout garante que a tool
    // antiga já saiu quando a nova for registrada.
    dono.addEventListener("abort", () => setTimeout(resolve, 0), { once: true });
  });
}

/** Conta no GTM uma chamada de tool feita por um agente. */
function gtmWebMcpTool(nome: string) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "webmcp_tool",
    webmcp: { nome_tool: nome },
  });
}

/**
 * Registra a lista de tools. Cada uma vai num try/catch próprio: um
 * `InvalidStateError` (nome repetido, schema inválido) numa tool não pode
 * derrubar as outras nem a página.
 *
 * O descriptor sai daqui com `execute` E `handler` apontando para a mesma
 * função: implementações novas leem `execute` e ignoram `handler`, as antigas
 * fazem o inverso. Sai de graça e cobre as duas.
 *
 * É essa mesma função que conta a chamada no GTM, então a medição vale para
 * qualquer uma das duas callbacks. Os forms declarativos (contato, sugestão e
 * senha) não passam por aqui e ficam fora da conta.
 */
export async function registerWebMcpTools(
  tools: WebMcpToolDescriptor[],
  signal: AbortSignal
): Promise<void> {
  const modelContext = getModelContext();
  if (!modelContext) return;

  for (const tool of tools) {
    await nomeLiberado(tool.name);
    if (signal.aborted) return;
    // O Chrome não manda o `context`; o safeExecute da tool preenche, como antes.
    const execute: WebMcpToolDescriptor["execute"] = (args, context) => {
      gtmWebMcpTool(tool.name);
      return tool.execute(args, context);
    };
    try {
      await modelContext.registerTool(
        {
          ...tool,
          execute,
          handler: (args: Record<string, any>) => execute(args, { signal }),
        },
        { signal }
      );
      donosDosNomes.set(tool.name, signal);
      signal.addEventListener(
        "abort",
        () => {
          if (donosDosNomes.get(tool.name) === signal) {
            donosDosNomes.delete(tool.name);
          }
        },
        { once: true }
      );
    } catch (error) {
      if (import.meta.dev) {
        console.warn(`[webmcp] falha ao registrar a tool "${tool.name}"`, error);
      }
    }
  }
}

/**
 * Registra um conjunto de tools pelo tempo de vida do escopo que chamou.
 *
 * - client-only e no-op quando o browser não tem WebMCP;
 * - só registra depois do aceite 18+, e registra sozinho quando o aceite vier;
 * - RE-REGISTRA quando `route.params.language` muda: é isso que faz as
 *   descrições serem bilíngues (o agente relê as tools no evento `toolchange`);
 * - desregistra ao sair da página (via `onScopeDispose`).
 *
 * `factory` recebe o contexto já montado (router + idioma + textos) e é
 * chamada a cada (re)registro. Devolve a função de limpeza, para quem chamar
 * de fora de um effect scope.
 */
export function useWebMcpTools(
  factory: (ctx: WebMcpToolsContext) => WebMcpToolDescriptor[]
): () => void {
  const noop = () => {};
  if (!import.meta.client || !isWebMcpAvailable()) return noop;

  // Capturado AQUI, onde ainda estamos no contexto do Nuxt (plugin ou setup).
  // A factory roda depois, em flush de watcher, onde `useRouter()` estouraria.
  const router = useRouter();
  const language = computed(() =>
    String(router.currentRoute.value.params.language ?? "") === "en"
      ? "en"
      : "pt-br"
  );
  const gateOpen = ref(hasAcceptedLegalWarning());

  let controller: AbortController | null = null;

  function unregister() {
    controller?.abort();
    controller = null;
  }

  function sync() {
    unregister();
    if (!gateOpen.value) return;
    controller = new AbortController();
    const ctx: WebMcpToolsContext = {
      router,
      language: language.value,
      t: webmcpTranslation(language.value),
    };
    // fire-and-forget: registrar é assíncrono, mas nada na página espera por
    // isso — e um erro já é tratado tool a tool dentro de registerWebMcpTools.
    void registerWebMcpTools(factory(ctx), controller.signal);
  }

  function onLegalAccepted() {
    gateOpen.value = true;
  }
  window.addEventListener(LEGAL_WARNING_ACCEPTED_EVENT, onLegalAccepted);

  const stopWatch = watch([language, gateOpen], sync, { immediate: true });

  const dispose = () => {
    stopWatch();
    window.removeEventListener(LEGAL_WARNING_ACCEPTED_EVENT, onLegalAccepted);
    unregister();
  };

  // Em página/componente o escopo cuida da limpeza; no plugin não há escopo e
  // as tools vivem enquanto o documento viver, que é o certo.
  if (getCurrentScope()) onScopeDispose(dispose);

  return dispose;
}
